import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { UpgradePrompt } from '../../components/overlays/UpgradePrompt';
import { UpgradeModalConfig } from '../types';

interface UpgradeModalContextType {
  showUpgradeModal: (config?: UpgradeModalConfig) => void;
  hideUpgradeModal: () => void;
  isVisible: boolean;
}

const UpgradeModalContext = createContext<UpgradeModalContextType | null>(null);

interface UpgradeModalProviderProps {
  children: ReactNode;
}

export const UpgradeModalProvider: React.FC<UpgradeModalProviderProps> = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [config, setConfig] = useState<UpgradeModalConfig>({});

  const showUpgradeModal = useCallback((modalConfig?: UpgradeModalConfig) => {
    setConfig(modalConfig || {});
    setIsVisible(true);
  }, []);

  const hideUpgradeModal = useCallback(() => {
    setIsVisible(false);
    setConfig({});
  }, []);

  return (
    <UpgradeModalContext.Provider value={{ showUpgradeModal, hideUpgradeModal, isVisible }}>
      {children}
      <UpgradePrompt
        visible={isVisible}
        onClose={hideUpgradeModal}
        feature={config.feature}
        description={config.description}
        source={config.source || 'feature_gate'}
      />
    </UpgradeModalContext.Provider>
  );
};

export function useUpgradeModal(): UpgradeModalContextType {
  const context = useContext(UpgradeModalContext);
  
  if (!context) {
    throw new Error('useUpgradeModal must be used within an UpgradeModalProvider');
  }
  
  return context;
}
